<script lang="ts">
    import { onDestroy } from 'svelte'

    import { DateTime } from 'luxon'

    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { simpleFetch, groups as ugroups } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const UPDATE_EVENTS_TIMEOUT_MS = 3 * 60 * 1000

    let eventsVsToday: 'past' | 'today' | 'futur' = $state('futur')
    let loading = $state(true)
    let updating = $state(false)
    let error = $state(false)
    let events: Event[] = []
    let eventDays: EventDay[] = $state([])

    interface Event {
        ctime: DateTime
        mtime: DateTime

        author_id: string

        due: DateTime
        text: string

        done: boolean

        // Added by the backend
        author_name: string

        // We compute this ourselves
        firstTextLine?: string
        textOtherLines?: string
    }
    interface EventDay {
        ymd: number
        date: DateTime
        past: boolean
        today: boolean
        items: Event[]
    }

    let eventTimer: ReturnType<typeof setTimeout> | undefined = undefined

    async function loadEvents() {
        let r: any

        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }

        if (!loading) updating = true
        error = false
        try {
            r = await simpleFetch('GET', '/json/event/1', { data: JSON.stringify({ done: false, next_days: 7, limit: 200 }, null, 0) })
        } catch (e) {
            console.error('Failed to load events:', e)
        }

        if (r) {
            importEventRes(r.data)
        } else {
            error = true
        }
        loading = false
        updating = false

        eventTimer = setTimeout(() => loadEvents(), UPDATE_EVENTS_TIMEOUT_MS)
    }

    onDestroy(() => {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }
    })

    if ($ugroups.has('global-event-admin')) {
        loadEvents()
    }

    function importEventRes(data: any) {
        events = data.items

        eventDays = []
        let localEVT: 'past' | 'today' | 'futur' | undefined = undefined
        // eventsVsToday = null
        let cYmd: EventDay | undefined = undefined

        let today = parseInt(DateTime.now().toFormat('yyyyMMdd'), 10)

        for (const item of events) {
            let lines = item.text.split('\n')
            item.firstTextLine = lines.shift()

            if (lines[0] === '') lines.shift()
            if (lines[lines.length - 1] === '') lines.pop()

            if (lines.length > 2) lines = [...lines.slice(0, 2).filter((l) => l), '…']

            item.textOtherLines = lines.join('\n')

            item.ctime = toLuxon(item.ctime)
            item.mtime = toLuxon(item.mtime)
            item.due = toLuxon(item.due)
            let ymd = parseInt(item.due.toFormat('yyyyMMdd'), 10)

            if (!cYmd || cYmd.ymd != ymd) {
                cYmd = {
                    ymd: ymd,
                    date: item.due,
                    past: ymd < today,
                    today: ymd == today,
                    items: [],
                }
                eventDays.push(cYmd)
                if (cYmd.past) localEVT = 'past'
                else if (cYmd.today && !localEVT) localEVT = 'today'
            }
            cYmd.items.push(item)
        }

        if (!localEVT) localEVT = 'futur'
        eventsVsToday = localEVT
    }
</script>

<style>
    .event-day:not(:first-child) {
        margin-top: 0.5em;
    }

    .event-date {
        font-weight: bold;
        padding: 2px 8px;
        background-color: #f7f7f7;
        border-radius: 3px;
        color: #999;
    }
    .today .event-date {
        color: #449;
    }
    .past .event-date {
        color: #b00;
    }
    .event {
        margin-left: 1.5em;
        margin-bottom: 0.5em;
    }
    .event:not(:last-child) {
        margin-bottom: 0.7em;
    }
    .author {
        float: right;
        font-size: 0.85em;
        margin-left: 2em;
    }
    .first-line {
        position: relative;
    }
    .bullet {
        position: absolute;
        left: -10px;
    }
    .other-lines {
        color: #999;
        font-size: 80%;
        white-space: pre-line;
    }

    .ptitle {
        flex: 1 0 0;
        color: inherit;
    }
</style>

<div class="card dashboard-events">
    <div class="card-title" class:text-warning={eventsVsToday == 'today'} class:text-danger={eventsVsToday == 'past'} class:text-secondary={eventsVsToday == 'futur'}>
        <a use:sanchor href={spath('/events?done=0')} class="ptitle"><Date value={'live'} format={'DATE_HUGE'} transform="ucfirst" /></a>
        {#if updating}
            <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
        {/if}
        <span style="font-feature-settings: 'tnum' 1, 'ss01' 1"><Date value={'live'} format={$lg == 'fr' ? 'H:mm:ss' : 'h:mm:ss a'} /></span>
    </div>

    {#if !$ugroups.has('global-event-admin')}
        <div class="card-body">
            <p>Bienvenue !</p>
        </div>
    {:else if loading}
        <div class="card-body d-flex gap-3">
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
            <div>{$_('Loading…')}</div>
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
        </div>
    {:else if error}
        <div class="card-body">
            <div class="text-danger">Les événements n’ont pas pu être chargés.</div>
        </div>
    {:else if !eventDays.length}
        <div class="card-body dashboard-events">
            <div class="no-event">Aucun événement dans les 7 prochains jours.</div>
        </div>
    {:else}
        <div class="card-body">
            {#each eventDays as eventDay}
                <div class="event-day" class:today={eventDay.today} class:past={eventDay.past}>
                    <div class="event-date"><Date value={eventDay.date} format={'DATE_HUGE'} transform="ucfirst" /></div>
                    {#each eventDay.items as item}
                        <div class="event">
                            {#if item.author_name}
                                <div class="author">{item.author_name}</div>
                            {/if}
                            <div class="first-line"><span class="bullet">•</span>{item.firstTextLine}</div>
                            <div class="other-lines">{item.textOtherLines}</div>
                        </div>
                    {/each}
                </div>
            {/each}
        </div>
    {/if}
</div>
